import { ReactNode } from "react";
import BrandMark from "@/components/auth/BrandMark";

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle: string;
}

// Tech due diligence feature highlights — static, no translation needed
const FEATURES = [
  "Architecture & system design review",
  "Software quality & technical debt analysis",
  "Security vulnerabilities & CVE scanning",
  "Dependency risk & licensing audit",
  "Scalability & infrastructure assessment",
  "Financial due diligence & valuation risk assessment",
  "Multi-agent findings & analysis",
  "AES-256 encryption per project of all uploaded documents, findings & analysis",
];

const AuthLayout = ({ children, title, subtitle }: AuthLayoutProps) => {
  return (
    <div className="flex min-h-screen gap-5 bg-brand-paper p-4 sm:p-5">
      {/* Left card - Branding */}
      <div className="brand-hero-surface hidden flex-col justify-between rounded-2xl border border-brand-line p-12 lg:flex lg:w-[55%]">
        {/* Wordmark */}
        <div className="flex items-center gap-3">
          <BrandMark />
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-ink-900">
            Perseon Labs · Veritas
          </span>
        </div>

        {/* Hero copy */}
        <div className="max-w-xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3">
            <span className="block h-px w-9 bg-brand-gold-500" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-gold-600">
              Due Diligence
            </span>
          </div>

          <h1 className="mt-5 font-display text-5xl font-medium leading-[1.1] tracking-tight text-brand-ink-900">
            Know the deal with agentic AI
            <br />
            before you close.
          </h1>

          <p className="mt-5 text-base leading-relaxed text-brand-ink-900/70">
            Surface hidden risks in acquisition targets — from financial exposure to operational
            inefficiencies — before it becomes your problem.
          </p>

          {/* Feature pills */}
          <div className="mt-8 flex flex-wrap gap-2">
            {FEATURES.map((feature) => (
              <span
                key={feature}
                className="rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs text-brand-ink-800"
              >
                <span className="mr-1.5 text-brand-gold-500" aria-hidden="true">
                  —
                </span>
                {feature}
              </span>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold-500/30 bg-brand-gold-500/10 px-3 py-1">
            <svg
              className="h-3 w-3 flex-shrink-0 text-brand-gold-600"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2l7 4v6c0 4.418-3.134 8.54-7 9.93C8.134 20.54 5 16.418 5 12V6l7-4z"
              />
            </svg>
            <span className="text-xs font-medium text-brand-gold-600">EU Data Center</span>
          </div>
          {/* <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold-500/30 bg-brand-gold-500/10 px-3 py-1">
            <svg className="h-3 w-3 flex-shrink-0 text-brand-gold-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-xs font-medium text-brand-gold-600">GDPR Compliant</span>
          </div> */}
          <p className="mt-3 text-xs tracking-wide text-brand-ink-900/70">
            © {new Date().getFullYear()} Perseon Labs · Veritas. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right card - Form */}
      <div className="flex w-full flex-col items-center justify-center rounded-2xl border border-brand-line bg-white px-6 py-12 shadow-[0_10px_30px_rgba(18,25,77,0.07)] lg:w-[45%]">
        <div className="w-full max-w-sm">
          {/* Mobile wordmark — the hero card (and its h1) is hidden below lg,
              so provide a top-level heading for assistive tech at those widths. */}
          <h1 className="sr-only lg:hidden">
            Perseon Labs · Veritas — know the deal with agentic AI before you close.
          </h1>
          <div className="mb-8 flex flex-col items-center gap-2 text-center lg:hidden">
            <BrandMark className="h-8 w-8" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink-900">
              Perseon Labs · Veritas
            </span>
            <p className="text-sm leading-relaxed text-brand-ink-900/70">
              Know the deal with agentic AI before you close.
            </p>
          </div>
{/* 
          <h2 className="font-display text-3xl font-medium tracking-tight text-brand-ink-900">
            {title}
          </h2>
          <p className="mt-2 text-sm text-brand-ink-900/60">{subtitle}</p>
*/}
          {/* Form Content */}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
